/**
 * Invoice & transaction arithmetic for form previews. The database recomputes
 * and stores the authoritative values (private.invoice_line_before / recalc_invoice);
 * the formulas here are kept identical and covered by parity tests.
 */
import { D, parseNumber, round2, sum, type Dec, type DecimalInput } from './money';

export interface LineInput {
  qty: DecimalInput;
  unitPrice: DecimalInput;
  discountPct?: DecimalInput;
  taxPct?: DecimalInput;
}

export interface LineTotals {
  subtotal: Dec;
  discount: Dec;
  net: Dec;
  tax: Dec;
  total: Dec;
}

export function lineTotals(l: LineInput): LineTotals {
  const subtotal = round2(D(l.qty).times(D(l.unitPrice)));
  const discount = round2(subtotal.times(D(l.discountPct)).dividedBy(100));
  const net = subtotal.minus(discount);
  const tax = round2(net.times(D(l.taxPct)).dividedBy(100));
  return { subtotal, discount, net, tax, total: net.plus(tax) };
}

export interface InvoiceTotals extends LineTotals {
  /** VAT per rate, keyed by the rate as a string ("20", "0") */
  taxByRate: Record<string, { base: Dec; tax: Dec }>;
}

export function invoiceTotals(lines: LineInput[]): InvoiceTotals {
  const each = lines.map(lineTotals);
  const taxByRate: InvoiceTotals['taxByRate'] = {};
  lines.forEach((l, i) => {
    const key = D(l.taxPct).toString();
    const t = each[i]!;
    const cur = taxByRate[key] ?? { base: D(0), tax: D(0) };
    taxByRate[key] = { base: cur.base.plus(t.net), tax: cur.tax.plus(t.tax) };
  });
  return {
    subtotal: sum(each.map((e) => e.subtotal)),
    discount: sum(each.map((e) => e.discount)),
    net: sum(each.map((e) => e.net)),
    tax: sum(each.map((e) => e.tax)),
    total: sum(each.map((e) => e.total)),
    taxByRate,
  };
}

/** A line the way the invoice form holds it: raw text straight out of the inputs. */
export interface DraftLine {
  description: string;
  qty: string;
  unitPrice: string;
}

/**
 * The lines the operator actually touched. The form always keeps one empty line
 * at the bottom and that one is dropped without a word; a half-written line is
 * kept, so the total on screen is always the total that gets saved (W-27).
 */
export function enteredLines<T extends DraftLine>(lines: T[]): T[] {
  return lines.filter((l) => l.description.trim() !== '' || (parseNumber(l.unitPrice)?.greaterThan(0) ?? false));
}

/**
 * Index of the first line that cannot be saved (no description, or a quantity
 * that is not positive), or −1 when every line is complete. Run it on
 * enteredLines(), never on the raw list.
 */
export function firstIncompleteLine(lines: DraftLine[]): number {
  return lines.findIndex((l) => !l.description.trim() || !(parseNumber(l.qty)?.greaterThan(0) ?? false));
}

/** VAT contained in a gross amount: 2.800 @ %20 → 466,67 (M-12 "KDV'yi ayır"). */
export function vatFromGross(gross: DecimalInput, pct: DecimalInput): Dec {
  const g = D(gross);
  const p = D(pct);
  if (p.isZero()) return D(0);
  return round2(g.minus(g.dividedBy(p.dividedBy(100).plus(1))));
}

export type TransactionType = 'receipt' | 'payment' | 'expense' | 'income' | 'transfer' | 'fee' | 'fx_diff';
export type Direction = 'in' | 'out';

export function directionOf(type: Exclude<TransactionType, 'fx_diff'>): Direction {
  return type === 'receipt' || type === 'income' ? 'in' : 'out';
}

export function signedAmount(direction: Direction, amount: DecimalInput): Dec {
  return direction === 'in' ? D(amount) : D(amount).negated();
}

export type PaymentStatus = 'draft' | 'open' | 'partially_paid' | 'paid' | 'cancelled';

export function paymentStatus(total: DecimalInput, paid: DecimalInput, current: PaymentStatus = 'open'): PaymentStatus {
  if (current === 'draft' || current === 'cancelled') return current;
  const t = D(total);
  const p = D(paid);
  if (t.greaterThan(0) && p.greaterThanOrEqualTo(t)) return 'paid';
  if (p.greaterThan(0)) return 'partially_paid';
  return 'open';
}

// ── payment ↔ invoice matching ───────────────────────────────────────────────
/** An open invoice the way v_open_items returns it. */
export interface OpenInvoiceLike {
  invoice_id?: string | null;
  currency?: string | null;
  remaining?: DecimalInput;
  remaining_base?: DecimalInput;
}

/**
 * The rate the invoice was posted with, read back from the row itself:
 * v_open_items carries what is left in both the invoice currency and the base
 * currency, and their ratio is that frozen rate. Null when nothing is left.
 */
export function openItemRate(item: OpenInvoiceLike): Dec | null {
  const remaining = D(item.remaining);
  if (remaining.isZero()) return null;
  return D(item.remaining_base).dividedBy(remaining);
}

/** Invoice-currency amount → the paying account's currency. Both rates are "base per unit". */
export function toAccountAmount(invoiceAmount: DecimalInput, invoiceRate: DecimalInput, accountRate: DecimalInput): Dec {
  const acc = D(accountRate);
  if (acc.isZero()) return D(0);
  return round2(D(invoiceAmount).times(D(invoiceRate)).dividedBy(acc));
}

/** The other way round: what an account-currency amount covers on the invoice. */
export function toInvoiceAmount(accountAmount: DecimalInput, accountRate: DecimalInput, invoiceRate: DecimalInput): Dec {
  const inv = D(invoiceRate);
  if (inv.isZero()) return D(0);
  return round2(D(accountAmount).times(D(accountRate)).dividedBy(inv));
}

export interface AllocationDraft {
  invoiceId: string;
  /** in the invoice's currency — payment_allocations.amount */
  amount: Dec;
  /** in the paying account's currency — payment_allocations.account_amount */
  accountAmount: Dec;
}

/** The account a payment leaves from / arrives in. */
export interface PayingAccount {
  currency?: string | null;
  /** base per unit of that currency (1 for the base currency itself) */
  rate?: DecimalInput;
}

/** The two documents settle one to one unless the money really changes currency. */
function sameCurrency(item: OpenInvoiceLike, account: PayingAccount): boolean {
  return !account.currency || !item.currency || account.currency === item.currency;
}

/**
 * What one invoice costs on the paying account. Same currency settles one to one
 * — a 1.000 $ invoice is closed with 1.000 $ whatever the two documents' rates
 * say, and private.allocation_before writes exactly that. Only a genuine currency
 * change goes through the rates.
 */
export function allocationLegs(item: OpenInvoiceLike, amount: DecimalInput, account: PayingAccount = {}): AllocationDraft | null {
  if (!item.invoice_id) return null;
  const invoiceAmount = round2(amount);
  if (invoiceAmount.lessThanOrEqualTo(0)) return null;
  const rate = openItemRate(item) ?? D(account.rate ?? 1);
  return {
    invoiceId: item.invoice_id,
    amount: invoiceAmount,
    accountAmount: sameCurrency(item, account) ? invoiceAmount : toAccountAmount(invoiceAmount, rate, account.rate ?? 1),
  };
}

/** The same pair read from the other end: this much money off the account, how much invoice does it close. */
function legsFromAccountAmount(item: OpenInvoiceLike, accountAmount: Dec, account: PayingAccount): AllocationDraft {
  const rate = openItemRate(item) ?? D(account.rate ?? 1);
  return {
    invoiceId: item.invoice_id!,
    amount: sameCurrency(item, account) ? accountAmount : toInvoiceAmount(accountAmount, account.rate ?? 1, rate),
    accountAmount,
  };
}

/**
 * Spread a payment over the contact's open invoices, oldest first. `total` is in
 * the paying account's currency; every item is settled in its own, so a lira
 * payment closes a dollar invoice and the database works the kur farkı out of the
 * two legs. An item covered in full gets its exact remaining amount, so rounding
 * never leaves a kuruş open.
 */
export function planAllocations(total: DecimalInput, items: OpenInvoiceLike[], account: PayingAccount = {}): AllocationDraft[] {
  let left = D(total);
  const drafts: AllocationDraft[] = [];
  for (const item of items) {
    if (left.lessThanOrEqualTo(0)) break;
    const full = allocationLegs(item, item.remaining, account);
    if (!full) continue;
    if (left.greaterThanOrEqualTo(full.accountAmount)) {
      drafts.push(full);
      left = left.minus(full.accountAmount);
      continue;
    }
    drafts.push(legsFromAccountAmount(item, round2(left), account));
    left = D(0);
  }
  return drafts;
}

/** An advance the way v_open_payments returns it. */
export interface OpenPaymentLike {
  transaction_id?: string | null;
  currency?: string | null;
  /** still free to hand to an invoice, in the account's currency */
  remaining?: DecimalInput;
  /** base per unit of that currency, frozen on the payment */
  fx_rate?: DecimalInput;
}

/**
 * The biggest match an advance and an invoice can make: whichever of the two runs
 * out first decides. Used by the invoice drawer, which matches the other way
 * round from the cash form — the payment already exists, the invoice is the one
 * on screen.
 */
export function matchPaymentToInvoice(item: OpenInvoiceLike, payment: OpenPaymentLike): AllocationDraft | null {
  const account: PayingAccount = { currency: payment.currency, rate: payment.fx_rate ?? 1 };
  const full = allocationLegs(item, item.remaining, account);
  if (!full) return null;
  const available = round2(payment.remaining);
  if (available.lessThanOrEqualTo(0)) return null;
  return full.accountAmount.lessThanOrEqualTo(available) ? full : legsFromAccountAmount(item, available, account);
}

/** A money movement the way listCompanyTransactions returns it. */
export interface SettlementLike {
  type?: string | null;
  amount_base?: DecimalInput;
}

/**
 * "Bu ay ödenen" / "Bu ay tahsil" on W-27: the cash that really moved in the
 * period, counted from the payment rows and not from the invoices they close —
 * an invoice written in August and paid in September belongs to September.
 * amount_base is frozen at the transaction's own rate, so a dollar payment
 * counts with the lira it actually cost.
 */
export function settlementTotalBase(rows: SettlementLike[], type: 'payment' | 'receipt'): Dec {
  return sum(rows.filter((r) => r.type === type).map((r) => r.amount_base ?? 0));
}

/**
 * Contact balance sign convention (company perspective): + = they owe us, − = we owe them.
 * UI label helper: "alacak" (receivable) / "borç" (payable) / "kapalı".
 */
export function balanceSide(balance: DecimalInput): 'receivable' | 'payable' | 'settled' {
  const b = D(balance);
  if (b.abs().lessThan('0.005')) return 'settled';
  return b.isPositive() ? 'receivable' : 'payable';
}

/** A cash / bank account row the way v_account_balances returns it. */
export interface AccountBalanceLike {
  type: string | null;
  currency?: string | null;
  is_closed?: boolean | null;
  /** balance in the account's own currency */
  balance?: DecimalInput;
  /** base-currency carrying value of that balance (v_account_balances.balance_base) */
  balance_base?: DecimalInput;
}

export interface CashTotal {
  /** every own account at its base-currency carrying value */
  total: Dec;
  /** a foreign account still holds money but no rate resolves it, so it counted as zero */
  missingRate: boolean;
}

/**
 * "Toplam karşılık" on W-28: the company's cash in base currency, so a USD or
 * EUR account counts with its carrying value instead of dropping out. A partner
 * current account is the partner's money, not the company's, and a closed
 * account cannot be spent — neither belongs in the total.
 * (cashflow.openingCashBase runs the same filter for the 12-week projection.)
 */
export function cashTotalBase(accounts: AccountBalanceLike[], baseCurrency = 'TRY'): CashTotal {
  const own = accounts.filter((a) => a.type !== 'partner' && !a.is_closed);
  return {
    total: sum(own.map((a) => a.balance_base ?? 0)),
    missingRate: own.some((a) => a.currency !== baseCurrency && !D(a.balance ?? 0).isZero() && D(a.balance_base ?? 0).isZero()),
  };
}

/** "Ortak — Mert · USD" already names its currency; other accounts get " · USD" appended. */
export function accountLabel(name: string | null | undefined, currency: string | null | undefined): string {
  const n = name ?? '';
  if (!currency || n.endsWith(` · ${currency}`) || n.endsWith(`(${currency})`)) return n;
  return `${n} · ${currency}`;
}
